import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Copy, Check, BookOpen, Headphones, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import PayPalCheckout from "@/components/PayPalCheckout";
import { completeGift, errorDetail } from "@/lib/reader";
import GiftCard from "@/components/reader/GiftCard";
import GiftStatusCheck from "@/components/reader/GiftStatusCheck";
import { useGiftOffer, isHolidayPrice, fmtPrice } from "@/lib/giftOffer";

function GiftForm({ onBought }) {
  const offer = useGiftOffer();
  const [fromName, setFromName] = useState("");
  const [message, setMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const handlePaid = async (orderId) => {
    try {
      onBought(await completeGift({ order_id: orderId, from_name: fromName || null, message: message || null }));
      toast.success("Payment received — here's your gift code!");
    } catch (e) {
      toast.error(errorDetail(e));
    }
  };
  const price = offer?.gift_price ?? 15;
  const product = { id: "reader-gift", title: "Leckie E-Reader Gift Pass — 40 days", price };
  return (
    <div className="border border-primary-gold/30 bg-[#121212]/90 backdrop-blur p-8 sm:p-10" data-testid="gift-buy-panel">
      {isHolidayPrice(offer) && (
        <span className="inline-block mb-4 font-mono-ui text-[10px] uppercase tracking-[0.25em] bg-primary-gold text-[#0B0C10] px-2 py-1" data-testid="gift-holiday-badge">
          Holiday price
        </span>
      )}
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="font-serif-display text-6xl" data-testid="gift-price">{fmtPrice(price)}</span>
        {isHolidayPrice(offer) && <span className="text-soft line-through font-mono-ui text-sm" data-testid="gift-regular-price">{fmtPrice(offer.regular_price)}</span>}
        <span className="text-soft font-mono-ui text-xs uppercase tracking-[0.18em]">one-off gift</span>
      </div>
      <label className="block mt-8 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-soft">From (optional)</label>
      <input value={fromName} onChange={(e) => setFromName(e.target.value)} maxLength={60} placeholder="Your name" data-testid="gift-from-input" className="mt-2 w-full bg-[#0B0C10] border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-primary-gold" />
      <label className="block mt-5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-soft">Personal message (optional)</label>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} rows={3} placeholder="Happy reading!" data-testid="gift-message-input" className="mt-2 w-full bg-[#0B0C10] border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-primary-gold resize-none" />
      <p className="text-right text-[10px] font-mono-ui text-soft mt-1">{message.length}/300</p>
      <button type="button" onClick={() => setShowPreview((v) => !v)} data-testid="gift-preview-toggle" className="mt-4 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:opacity-80">
        {showPreview ? "Hide card preview" : "Preview the printable gift card"}
      </button>
      {showPreview && <GiftCard preview gift={{ code: "GIFT-XXXX-XXXX", from_name: fromName.trim() || null, message: message.trim() || null }} />}
      <div className="mt-6">{offer ? <PayPalCheckout product={product} onPaid={handlePaid} /> : null}</div>
    </div>
  );
}

function GiftResult({ gift }) {
  const [copied, setCopied] = useState(null);
  const link = `${window.location.origin}/reader`;
  const opener = gift.from_name ? `${gift.from_name} has gifted you` : "You've been gifted";
  const shareText = `${opener} 40 days of the Leckie E-Reader — all four Michael Leckie novels to read and listen to.${gift.message ? `\n\n“${gift.message}”` : ""}\n\nYour gift code: ${gift.code}\nSign up or sign in at ${link} and choose "Have a gift code?" to start reading.`;
  const copy = async (what, text) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };
  return (
    <div className="border border-primary-gold/40 bg-[#121212]/90 p-8 sm:p-10" data-testid="gift-result">
      <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold">Your gift code</p>
      <p className="font-mono-ui text-3xl sm:text-4xl tracking-[0.12em] mt-4 break-all" data-testid="gift-code">{gift.code}</p>
      <p className="text-soft text-sm mt-4">Save this code — we've also emailed it, with a link to your printable card, to your account or PayPal email. Pass it to your friend; they redeem it on the E-Reader page for 40 days of access.</p>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => copy("code", gift.code)} data-testid="gift-copy-code" className="btn-gold px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] flex items-center gap-2">
          {copied === "code" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy code
        </button>
        <button onClick={() => copy("text", shareText)} data-testid="gift-copy-message" className="btn-ghost px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] flex items-center gap-2">
          {copied === "text" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy gift message
        </button>
      </div>
      <pre className="mt-6 whitespace-pre-wrap text-sm text-soft border-l-2 border-primary-gold/40 pl-4 font-sans" data-testid="gift-share-text">{shareText}</pre>
      <GiftCard gift={gift} />
    </div>
  );
}

export default function ReaderGift() {
  const [gift, setGift] = useState(null);
  return (
    <div className="pt-32 pb-16 px-6 sm:px-8 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-testid="reader-gift-page">
      <div className="lg:col-span-7 fade-up">
        <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold flex items-center gap-2"><Gift className="w-4 h-4" /> Give the E-Reader</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">A library, <span className="text-primary-gold italic">gift-wrapped.</span></h1>
        <p className="text-soft text-base mt-6 max-w-xl leading-relaxed">Buy a 40-day E-Reader pass for someone else. You'll get a gift code to pass on; their 40 days start when they redeem it.</p>
        <ul className="mt-10 space-y-4 text-text-primary">
          <li className="flex gap-4"><BookOpen className="w-5 h-5 text-primary-gold" /> All four Michael Leckie novels</li>
          <li className="flex gap-4"><Headphones className="w-5 h-5 text-primary-gold" /> Read aloud by a male or female narrator</li>
          <li className="flex gap-4"><CalendarClock className="w-5 h-5 text-primary-gold" /> 40 days from the day it's redeemed</li>
        </ul>
        <Link to="/reader" data-testid="gift-back-to-reader" className="inline-block mt-10 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold">← Back to the E-Reader</Link>
        <GiftStatusCheck />
      </div>
      <div className="lg:col-span-5 w-full">{gift ? <GiftResult gift={gift} /> : <GiftForm onBought={setGift} />}</div>
    </div>
  );
}
